// Cluster leaderboard — a ranked list carrying many attributes per row, so a table,
// not a chart. Each row is ONE owned signal: blast radius, classification, trend,
// owner. Colour appears only as the classification chip beside its text label, never
// as the text colour itself.
import { Fragment, useEffect } from 'react';
import { titleCase } from '../lib/format';
import { ClassificationChip, ObservedOnlyChip, OriginChip, VersionChip } from './shared/Chip';
import FixCard from './FixCard';

export default function Leaderboard({ clusters, mode, onSelect, selectedId, runFilter = null, onClearRun = null }) {
  // A selected signal scrolls into view — deep links land mid-page, and an
  // expansion below the fold reads as "nothing happened" (the incident-link
  // review: two different signals looked like the same page).
  useEffect(() => {
    if (!selectedId) return;
    document.getElementById(`signal-${selectedId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selectedId]);

  return (
    <section className="card card--flat">
      <header className="card__head">
        <div>
          <h2 className="card__title">Signals, ranked by blast radius</h2>
          <p className="card__subtitle">
            {clusters.length} signal{clusters.length === 1 ? '' : 's'}
            {runFilter ? ` in run …${String(runFilter).slice(-5)}` : ' in the latest run'}
            {/* The tally a developer needs: signals must account for the run's
                failures (plus any "unaccounted" strip below). */}
            {clusters.some((c) => c.members_in_run != null) && (
              <> · covering {clusters.reduce((n, c) => n + (c.members_in_run || 0), 0)} of this run's failures</>
            )}
            {' '}· select a row for the evidence and the fix card
          </p>
        </div>
      </header>
      <div className="card__table">
        <table className="data-table data-table--rows">
          <thead>
            <tr>
              <th className="num">Impact</th>
              <th>Signal</th>
              <th>Kind</th>
              <th>Owner</th>
              <th className="num">Unblocks</th>
              <th>Release</th>
              <th>Trend</th>
            </tr>
          </thead>
          <tbody>
            {clusters.map((c) => (
              <Fragment key={c.cluster_id}>
                <tr
                  id={`signal-${c.cluster_id}`}
                  className={selectedId === c.cluster_id ? 'is-selected' : ''}
                  tabIndex={0}
                  role="button"
                  aria-pressed={selectedId === c.cluster_id}
                  aria-expanded={selectedId === c.cluster_id}
                  onClick={() => onSelect(c.cluster_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(c.cluster_id);
                    }
                  }}
                >
                  <td className="num strong">{c.impact_score}</td>
                  <td>
                    {/* Humanized display title (MCIP-27); the raw stored title
                        stays honest as the hover text. */}
                    <span className="signal__title" title={c.title}>{c.display_title || c.title}</span>
                    {c.bug_candidate && <span className="tag tag--bug">Probable bug</span>}
                    <ObservedOnlyChip observedOnly={c.observed_only} />
                    <OriginChip origins={c.origins} />
                    {/* Tally line: this run's share vs the cluster's whole
                        history — the page must add up to the run's failed count.
                        Observed-only signals have no failing tests to tally: they
                        say what they are instead of claiming zero failures. */}
                    {c.observed_only ? (
                      <span className="muted" style={{ display: 'block', fontSize: 12 }}>
                        no test failed — a passing browser flow witnessed this backend error
                      </span>
                    ) : c.members_in_run != null && (
                      <span className="muted" style={{ display: 'block', fontSize: 12 }}>
                        {c.members_in_run} failure{c.members_in_run === 1 ? '' : 's'} in this run
                        {c.member_count > c.members_in_run && ` · ${c.member_count} across all runs (recurring)`}
                      </span>
                    )}
                  </td>
                  <td><ClassificationChip classification={c.classification} mode={mode} /></td>
                  <td>
                    {c.owner_pod || <span className="muted">unresolved</span>}
                  </td>
                  <td className="num">{c.expected_unblock_count}</td>
                  {/* MCIP-24: version the failure happened on — omitted (—) when
                      no stored row carries one, never guessed. */}
                  <td>
                    {c.release_at_failure
                      ? <VersionChip version={c.release_at_failure} source={c.release_source} observed={c.release_observed || []} />
                      : <span className="muted">—</span>}
                  </td>
                  <td className="muted">{c.trend_bucket || '—'}</td>
                </tr>
                {/* Unified drill-in: the row expands to the full signal — the union of
                    the former Human detail panel (summary, confidence, member tests)
                    and the former Developer fix card (evidence, projection, PR prompt,
                    dispatch). The developer-view row already carries all of it, so
                    expanding never refetches. Clicks inside must not re-toggle the row. */}
                {selectedId === c.cluster_id && (
                  <tr className="fixcard-row" onClick={(e) => e.stopPropagation()}>
                    <td colSpan={7}>
                      <p className="detail__meta">
                        {c.cluster_id} · {titleCase(c.classification)} at confidence {c.confidence} ·{' '}
                        {c.owner_pod || 'owner unresolved'} · impact {c.impact_score}
                      </p>
                      {c.summary && <p className="detail__summary">{c.summary}</p>}
                      {/* Deploy-window suspect (no GitHub needed): present only when
                          the API could compute it honestly from stored run rows. */}
                      {c.suspect_deploy && (
                        <p className="detail__meta">
                          <span className="tag tag--bug">Suspect deploy</span>{' '}
                          arrived with {c.suspect_deploy.version} — previous clean run
                          {' '}(#{c.suspect_deploy.last_clean_run}) had {c.suspect_deploy.previous_version}
                          {c.suspect_deploy.versions_between?.length > 0 && (
                            <> · deploys in between: {c.suspect_deploy.versions_between.join(', ')}</>
                          )}
                          {c.suspect_deploy.compare_url && (
                            <>
                              {' '}·{' '}
                              <a className="run-link" href={c.suspect_deploy.compare_url} target="_blank" rel="noreferrer">
                                what changed ↗
                              </a>
                            </>
                          )}
                        </p>
                      )}
                      <FixCard cluster={c} />
                      <h3 className="detail__h3">Tests in this signal ({c.tests?.length || 0})</h3>
                      <ul className="test-list">
                        {c.tests?.map((t) => (
                          <li key={t.failure_id}>
                            <code>{t.test_name}</code>
                            {t.failure_stage && t.failure_stage !== 'test' && <span className="tag">{t.failure_stage}</span>}
                            {t.ci_test_url && (
                              <a className="tag" href={t.ci_test_url} target="_blank" rel="noreferrer">DD test runs ↗</a>
                            )}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
